<div style="padding-top: 30px; padding-bottom: 10px; ">
    <p class="mat-headline-3">
        Privacy Policy OurOMC.ie
    </p>
</div>
<div mat-dialog-content style="margin-left: 40px; margin-top: 0px" class="mat-typography">
    <p class="mat-headline-4">Our Role: Data Processor</p>
    <p class="mat-body">
        For the purposes of the General Data Protection Regulation (GDPR), 
        Coburn Social Enterprise acts solely as a <strong>Data Processor</strong>. 
        We provide the Software and the necessary infrastructure for its operation. 
        We do not determine the purposes or means of the processing of personal data. 
        We process personal data strictly on the basis of the configuration rules determined by users 
        and the access privileges they determine should be granted, as evidenced through their configuration of the access controls.
    </p>

    <p class="mat-headline-4">The Data Controller: The OMC</p>
    <p class="mat-body">
        The <strong>Data Controller</strong> is the Owners' Management Company (OMC) or the entity that has licensed the Software for its use. The Data Controller is ultimately responsible for all personal data collected, stored, and processed within the Software. This responsibility includes:
    </p>
    <ul class="mat-body">
        <li>Determining the legal basis for processing personal data.</li>
        <li>Ensuring that data is collected and used in a fair and transparent manner.</li>
        <li>Responding to data subject access requests.</li>
        <li>Ensuring the accuracy of the data.</li>
        <li>Ensuring that data is processed in accordance with the principles of data protection, including the principle of 'data minimization.'</li>
    </ul>
    <p class="mat-body">
        While an OMC may delegate administrative tasks and responsibilities to a managing agent, 
        the OMC remains ultimately accountable to ensure compliance with all applicable data protection laws.
    </p>

    <p class="mat-headline-4">Data Processed</p>
    <p class="mat-body">
        The types of personal data processed through the Software are determined by the Data Controller and may include, but are not limited to:
    </p>
    <ul class="mat-body">
        <li>User account information (e.g., name, email address, contact number).</li>
        <li>Property ownership details.</li>
        <li>Communications, Documents and Votes all shared through user defined channels.</li>
        <li>Full financial accounts, including budgets, service charges, monies received, bank balances, purchase invoices, and payments to suppliers.</li>
    </ul>

    <p class="mat-headline-4">Responsibility of the Data Controller</p>
    <p class="mat-body">
        The Data Controller is responsible for the appropriate use of the Software's features to ensure data privacy. This includes:
    </p>
    <ul class="mat-body">
        <li><strong>Access Control and the principle of least privilege:</strong> The Data Controller is responsible for configuring roles and permissions to ensure that users are only granted access to the information strictly necessary for their job or role (the principle of 'least privilege'). This includes setting up custom roles and configuring page access rights.</li>
        <li><strong>Channel Management:</strong> The Data Controller, or their designated Administrator, is responsible for creating and managing channels. They must ensure that the audience for each channel is correctly configured and that the information (e.g., documents, communications) shared within those channels is appropriate for the individuals who have access to them.</li>
        <li><strong>Data Minimisation:</strong> The Data Controller should ensure that only necessary personal data is collected and stored within the Software.</li>
    </ul>

    <p class="mat-body">
        <strong>Best Practice Notice: Testing Mail Merges</strong>
    </p>
    <p class="mat-body">
        <strong>Recommended:</strong> We strongly advise setting up a dummy OMC and/or contacts (with non-real names and sample balances) when sending test emails to review layouts and formatting.
    </p>
    <p class="mat-body">
        <strong>Using Real Contact Data:</strong> If you choose to send test emails containing real customer details (such as names, addresses, outstanding balances, etc.) to your own inbox, you should verify this with your data protection officer. At minimum:
    </p>
    <ul class="mat-body">
        <li>Ensure your organisation's Privacy Policy and Legitimate Interest Assessment (LIA) cover internal testing using live personal data under GDPR/data protection laws.</li>
        <li>Ensure that any such test emails are deleted immediately once reviewed.</li>
        <li>Remember that test emails sent across email networks pass through logs and servers where those personal details may be retained for up to three (3) months.</li>
        <li>Users choosing to send test mails are responsible for ensuring that it is permissible to do so.</li>
    </ul>

    <p class="mat-headline-4">Our Responsibilities as Data Processor & Use of Sub-processors</p>
    <p class="mat-body">
        As a Data Processor, we are committed to:
    </p>
    <ul class="mat-body">
        <li>Implementing and maintaining appropriate technical and organizational measures to ensure a level of security appropriate to the risk of processing.</li>
        <li>Assisting the Data Controller in meeting their GDPR obligations, including responding to data subject requests and security breach notifications.</li>
        <li>Processing personal data only on the documented instructions of the Data Controller.</li>
        <li>Ensuring that personnel who have access to personal data are bound by a duty of confidentiality.</li>
    </ul>
    <p class="mat-body">
        To provide our services, we engage trusted third-party service providers ("Sub-processors"). 
        We maintain a list of our current Sub-processors, which is published as part of our <a routerLink="/dataProcessingAgreement">data processing agreement</a>. 
        We perform due diligence on all our Sub-processors to ensure they meet our standards for data protection and security.
        The current list of sub processors is included in the Data Processing Agreement.
    </p>
    <p class="mat-body">
        Please note that we may change or add new Sub-processors from time to time. 
        This allows us to maintain flexibility and operational efficiency while ensuring that all 
        Sub-processors comply with data protection regulations.
    </p>
    <p class="mat-headline-4">Data Retention</p>
    <p class="mat-body">
        The Data Controller is responsible for establishing and implementing a comprehensive data retention policy for 
        all personal data stored in the Software. This policy must be in compliance with GDPR's 'storage limitation' 
        principle, ensuring that personal data is not kept for longer than is necessary for the purposes for which it 
        was collected.
    </p>
    <p class="mat-body">
        The OMC, as the Data Controller, must determine appropriate retention periods based on a combination of factors, 
        including:
    </p>
    <ul class="mat-body">
        <li><strong>Legal and Statutory Obligations:</strong> Certain data, such as financial accounts and the statutory register of members, must be retained for specific periods as mandated by law (e.g., the Companies Act 2014 in Ireland).</li>
        <li><strong>The Duration of the Relationship:</strong> We will retain an OMC's data for the duration of its relationship with us. Once this relationship ends, we will retain the data for a period of seven (7) years to allow the Data Controller to fulfill their legal and operational obligations (e.g., for audits and historical reference). After this period, the data will be securely deleted.</li>
        <li><strong>Specific Business Purposes:</strong> Data required for ongoing operations (e.g., communications, documents) should be deleted when no longer necessary.</li>
    </ul>
    <p class="mat-body">
        <strong>Specific Data Categories and Retention</strong>
    </p>
    <ul class="mat-body">
        <li><strong>Financial Records:</strong> The OMC, as a corporate entity, is legally required to retain accounting records, including budgets, invoices, and bank statements, for a period of at least six (6) years after the end of the financial year to which they relate. Our systems facilitate the storage of this data for this purpose.</li>
        <li><strong>Register of Members:</strong> The Companies Act requires the company to maintain a register of past and present members. This is a statutory record and, therefore, personal data contained within it (names, addresses, dates of membership) must be retained indefinitely to comply with this legal obligation.</li>
        <li><strong>Other Data:</strong> All other personal data must be processed and retained in accordance with the OMC's own data retention policy and deleted when the purpose for its collection has expired.</li>
    </ul>
    <p class="mat-body">
        We provide a service, on request, to perform permanent deletion of data. 
        In the future this may be fully automated, for now, requests should be submitted by mail.
        The Data Controller is responsible to manage data and initiate deletion requests. 
        The Data Controller is responsible for the timely deletion of data that is no longer necessary. 
        We do not retain data indefinitely by default; retention periods are determined by legal and contractual obligations, 
        and it is the Data Controller's responsibility to manage these.
    </p>

    <p class="mat-headline-4">Contact Information</p>
    <p class="mat-body">
        If you have any questions about this Privacy Policy or the processing of your personal data, please contact the Data Controller (your Owners' Management Company or its designated managing agent).
    </p>
    <p class="mat-body">
        For technical queries relating to the software itself, you may contact Coburn Social Enterprise, but please note we cannot assist with data access requests or other privacy-related matters as we are not the Data Controller.
    </p>
</div>